/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../hooks';
import { useField } from '@unform/core';
import { ProdutosService } from '../services/api/estoque/produtos/ProdutosService';

type TAutoCompleteOption = {
  idprod: string;
  label: string;
  descricao: string;
  marca: string;
  vencimento: boolean;
  referencia: string;
  precoSaida: number;
  precovenda: number;
  idproduto: string;
  idref: string;
  quan: number;
  un: string;
};

interface IAutoCompleteProdutoProps {
  isExternalLoading?: boolean;
  filtro?: string;

  un?: (valor: string | undefined) => void;
  referencia?: (valor: string | undefined) => void;
  descricao?: (valor: string | undefined) => void;
  marca?: (valor: string | undefined) => void;
  vencimento?: (valor: boolean | undefined) => void;
  precoSaida?: (valor: number | undefined) => void;
  pVenda?: (valor: number | undefined) => void;
  quan?: (valor: number | undefined) => void;
  idproduto?: (valor: string | undefined) => void;
  idref?: (valor: string | undefined) => void;
}

export const AutoCompleteProduto: React.FC<IAutoCompleteProdutoProps> = ({
  isExternalLoading = false,
  referencia,
  descricao,
  vencimento,
  precoSaida,
  pVenda,
  marca,
  un,
  quan,
  idproduto,
  idref,
  filtro = 'descricao',
}) => {
  const { fieldName, registerField, defaultValue, error, clearError } = useField('idprodutos');
  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');
  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ProdutosService.getAlllist(busca, selectedId, filtro)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            return;
          }
          if (filtro === 'descricao') {
            setOpcoes(
              result.data.map((produto: any) => ({
                idprod: produto.idproduto,
                label: produto.referencia
                  ? `${produto.descricao} - ${produto.referencia}`
                  : produto.descricao,
                referencia: produto.referencia,
                marca: produto.marca,
                descricao: produto.descricao,
                vencimento: produto.vencimento ?? false,
                precoSaida: produto.precosaida,
                precovenda: produto.pvenda,
                quan: produto.quan,
                idproduto: produto.idproduto,
                idref: produto.idref,
                un: produto.un,
              }))
            );
          } else if (filtro === 'referencia') {
            setOpcoes(
              result.data.map((produto) => ({
                idprod: produto.idproduto,
                label: produto.descricao + ' - ' + produto.referencia || '',
                referencia: produto.referencia,
                descricao: produto.descricao,
                marca: produto.marca,
                vencimento: produto.vencimento,
                precoSaida: produto.precosaida,
                un: produto.un,
                precovenda: produto.pvenda,
                quan: produto.quan,
                idref: produto.idref,
                idproduto: produto.idproduto,

              })),
            );
          }
        });
    });
  }, [busca, selectedId, filtro, debounce]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;
    const selectedOption = opcoes.find((opcao) => opcao.idprod === selectedId);
    return selectedOption ?? null;
  }, [selectedId, opcoes]);
  useEffect(() => {
    if (autoCompleteSelectedOption) {
      pVenda?.(autoCompleteSelectedOption.precovenda);
      precoSaida?.(autoCompleteSelectedOption.precoSaida);
      quan?.(autoCompleteSelectedOption.quan);
    }
  }, [autoCompleteSelectedOption]);

  return (
    <Autocomplete
      openText="Abrir"
      closeText="Fechar"
      noOptionsText="Sem opções"
      loadingText="Carregando..."
      disablePortal
      options={opcoes}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, value) => option.idprod === value.idprod}
      onInputChange={(_, newValue) => setBusca(newValue)}
      onChange={(_, newValue) => {
        setSelectedId(newValue?.idprod);
        idproduto?.(newValue?.idprod);
        idref?.(newValue?.idref);
        referencia?.(newValue?.referencia);
        marca?.(newValue?.marca);
        descricao?.(newValue?.descricao);
        un?.(newValue?.un);
        vencimento?.(newValue?.vencimento);
        precoSaida?.(newValue?.precoSaida);
        pVenda?.(newValue?.precovenda);
        setBusca('');
        clearError();
      }}
      popupIcon={isLoading ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Pesquisar ítem"
          error={!!error}
          helperText={error}
        />
      )}
    />
  );
};
